import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  SortDirection,
  useNotify
} from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoAdhesionCFlotante } from '../../../api'
import { VirtualBatteryBalanceDetailModel } from '../../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import { enumToSelectOptionArray } from '../../../helpers'
import { getCustomerDomain } from '../../../redux'
import { VirtualBatteryBalanceTableModel } from '../floatingConcepts.model'
import { fetchVirtualBatteryBalanceDetailList } from '../floatingConcepts.service'
import { VirtualBatteryDetailPanel } from './components/virtualBatteryDetailPanel'
import { mapDetailModelArrayToTableModel } from '../floatingConcepts.mapper'

export interface VirtualBatteryDetailPage {
  subjectId: number
  subjectDescription: string
  contractId?: number
  contractCode?: string
  settlementGroupId?: number
  settlementGroupDescription?: string
  balance?: number
  codeColumn: string
}

export interface VirtualBatteryDetailPageProps {
  location?: {
    state: VirtualBatteryDetailPage
  }
}

export const VirtualBatteryDetailPage: React.FC<VirtualBatteryDetailPageProps> = props => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [virtualBatteryDetailList, setVirtualBatteryDetailList] = useState<
    VirtualBatteryBalanceDetailModel[]
  >([])

  const [t] = useTranslation(['globalerisLibrary', 'floatingConcepts', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()
  const propsData =
    props && props.location
      ? { ...props?.location?.state }
      : ({} as VirtualBatteryBalanceTableModel)
  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  const calculateBalance = (
    virtualBatteryDetailList: VirtualBatteryBalanceDetailModel[]
  ): number => {
    const balance = virtualBatteryDetailList.reduce((acc, item) => acc + item.amount, 0)
    return parseFloat(balance.toFixed(2))
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.SIMPLE,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    customInsertEditModal: false,
    sortOrder: { name: 'emissionDate', direction: SortDirection.ASC },
    analytics: {
      customer: getCustomerDomain(),
      view: t('floatingConcepts:dataTable.analytics')
    }
  }

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'emissionDate',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.emisionDate'),
      type: DataType.DATE
    },
    {
      name: 'originInvoiceCode',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.originInvoice'),
      type: DataType.TEXT
    },
    {
      name: 'cupsCode',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.cups'),
      type: DataType.TEXT
    },
    {
      name: 'adhesionType',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.adhesionType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoAdhesionCFlotante,
        'enums:EnumTipoAdhesionCFlotante'
      )
    },
    {
      name: 'startDate',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.endDate'),
      type: DataType.DATE
    },
    {
      name: 'quantity',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.quantity'),
      type: DataType.NUMBER
    },
    {
      name: 'price',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.price'),
      type: DataType.NUMBER
    },
    {
      name: 'amount',
      label: t('floatingConcepts:components.virtualBatteryDetail.dataTable.amount'),
      type: DataType.NUMBER
    }
  ]
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    const getVirtualBatteryBalanceDetailList = async () => {
      setIsFetching(true)
      if (!propsData.contractId && !propsData.settlementGroupId) {
        setVirtualBatteryDetailList([])
        return
      }
      await fetchVirtualBatteryBalanceDetailList(
        propsData.contractId ?? 0,
        propsData.settlementGroupId ?? 0
      ).then(response => {
        if (response.success && response.data) {
          setVirtualBatteryDetailList(mapDetailModelArrayToTableModel(response.data))
        } else {
          error(t('floatingConcepts:notifications.getVirtualBatteryDetailList.error'))
        }
      })
      setIsFetching(false)
    }

    getVirtualBatteryBalanceDetailList()
  }, [])
  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN}>
      <PageLeftColumn
        title={t('floatingConcepts:components.virtualBatteryDetail.leftColumn.title')}
      >
        <VirtualBatteryDetailPanel
          isLoading={isFetching}
          subjectDescription={propsData?.subjectDescription}
          contractOrGroupCode={propsData?.codeColumn}
          balance={propsData?.balance ?? calculateBalance(virtualBatteryDetailList)}
        />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('floatingConcepts:components.virtualBatteryDetail.title')}>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              history.goBack()
            }}
          >
            {t('floatingConcepts:components.virtualBatteryDetail.actions.back')}
          </Button>
        </PageHeader>
        {isFetching ? (
          <DataTableSkeleton />
        ) : (
          <DataTable data={virtualBatteryDetailList} columns={columns} options={options} />
        )}
      </PageCenterColumn>
    </Page>
  )
}
